import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { HelpCircle, ShieldAlert } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { toast } from 'sonner';

/**
 * KeyboardShortcuts
 * -----------------
 * Wave 2.1 — global G-prefixed navigation shortcuts + ? help dialog.
 * Wave 4.03 — added `G N` (Notifications), `G U` (Mountings) and a hidden
 * Super-Admin power combo `Alt + D D D` that toggles the Maintenance Lockdown.
 * Wave 4.10h — added `G O` (Production Digest).
 *
 * Implementation notes:
 *  • Two-key sequences (e.g. `G` then `H`) — first press of `G` (without modifier)
 *    arms a 1.5-second window during which the second key triggers navigation.
 *  • Shortcuts NEVER fire while focus is in an editable element (input,
 *    textarea, contenteditable). This avoids surprising users mid-typing.
 *  • A single key `?` (Shift + /) opens the help dialog from anywhere.
 *  • A "?" button rendered in the topbar provides the same access via mouse.
 *  • `Alt + D` pressed three times in quick succession (Super Admin only)
 *    opens a confirmation dialog to turn the global Maintenance Lockdown
 *    on/off. Deliberately NOT advertised in the help dialog.
 */

const SHORTCUTS = [
  { keys: ['G', 'H'], label: 'Dashboard', path: '/dashboard' },
  { keys: ['G', 'N'], label: 'Notifications', path: '/notifications' },
  { keys: ['G', 'L'], label: 'Gold', path: '/gold' },
  { keys: ['G', 'U'], label: 'Mountings', path: '/mountings' },
  { keys: ['G', 'D'], label: 'Diamonds', path: '/diamonds' },
  { keys: ['G', 'C'], label: 'Color Stones', path: '/color-stones' },
  { keys: ['G', 'P'], label: 'Pearls', path: '/pearls' },
  { keys: ['G', 'F'], label: 'Order Forms', path: '/orders' },
  { keys: ['G', 'T'], label: 'Customers', path: '/customers' },
  { keys: ['G', 'J'], label: 'Jewellery', path: '/jewellery' },
  { keys: ['G', 'V'], label: 'Vendors', path: '/vendors' },
  { keys: ['G', 'G'], label: 'Goldsmiths', path: '/goldsmiths' },
  { keys: ['G', 'R'], label: 'Reports', path: '/reports' },
  { keys: ['G', 'O'], label: 'Production Digest', path: '/reports/production-stage-digest' },
  { keys: ['G', 'E'], label: 'Estimated Delivery', path: '/reports/estimated-delivery' },
  { keys: ['G', 'A'], label: 'Activity Digest', path: '/reports/digest' },
  { keys: ['G', 'M'], label: 'Memos', path: '/reports/memos' },
];

function isInteractiveTarget(el) {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  // Honour ARIA editable hints
  const role = el.getAttribute && el.getAttribute('role');
  if (role === 'combobox' || role === 'textbox') return true;
  return false;
}

export default function KeyboardShortcuts({ onShowHelp }) {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  // Track the leader key + timestamp so we can implement a windowed sequence.
  const leader = useRef({ key: null, ts: 0 });
  // Visual hint for in-progress sequence (top-right floating toast-style)
  const [leaderHint, setLeaderHint] = useState(false);

  // ---------------------------------------------------------------------
  // G-prefixed navigation sequences + "?" help.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const SEQ_WINDOW_MS = 1500;
    const onKeyDown = (e) => {
      // Don't intercept when focus is in inputs / editable fields.
      if (isInteractiveTarget(e.target)) return;
      // Ignore when any modifier other than Shift is pressed (we use Shift for "?")
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const key = e.key;
      // `?` opens the help dialog
      if (key === '?' || (e.shiftKey && key === '/')) {
        e.preventDefault();
        onShowHelp && onShowHelp();
        leader.current = { key: null, ts: 0 };
        setLeaderHint(false);
        return;
      }
      // Single-letter `g` arms the leader key
      const upper = (key || '').toUpperCase();
      if (upper === 'G' && !leader.current.key) {
        leader.current = { key: 'G', ts: Date.now() };
        setLeaderHint(true);
        // Auto-disarm after the window expires
        setTimeout(() => {
          if (Date.now() - leader.current.ts >= SEQ_WINDOW_MS) {
            leader.current = { key: null, ts: 0 };
            setLeaderHint(false);
          }
        }, SEQ_WINDOW_MS + 50);
        return;
      }
      // Awaiting second key after G
      if (leader.current.key === 'G' && (Date.now() - leader.current.ts) <= SEQ_WINDOW_MS) {
        const hit = SHORTCUTS.find((s) => s.keys[0] === 'G' && s.keys[1] === upper);
        if (hit) {
          e.preventDefault();
          navigate(hit.path);
        }
        leader.current = { key: null, ts: 0 };
        setLeaderHint(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, onShowHelp]);

  // ---------------------------------------------------------------------
  // Alt + D×3 → toggle Maintenance Lockdown (Super Admin only, hidden).
  // ---------------------------------------------------------------------
  const [lockConfirm, setLockConfirm] = useState({
    open: false, current: false, busy: false, loading: false,
  });

  const openLockdownConfirm = useCallback(async () => {
    // Open immediately in a "checking…" state, then hydrate with the live flag.
    setLockConfirm({ open: true, current: false, busy: false, loading: true });
    try {
      const { data } = await api.get('/system/portal-access');
      setLockConfirm({ open: true, current: !!data.portal_lockdown, busy: false, loading: false });
    } catch {
      setLockConfirm({ open: false, current: false, busy: false, loading: false });
      toast.error('Could not read portal status. Please try again.');
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    const WINDOW_MS = 1200;
    let count = 0;
    let firstTs = 0;
    const onKey = (e) => {
      const isD = e.code === 'KeyD' || (e.key || '').toLowerCase() === 'd';
      // Require Alt held WITHOUT Ctrl/Meta so AltGr combos don't false-trigger.
      if (!(e.altKey && !e.ctrlKey && !e.metaKey && isD)) return;
      // Alt+D normally focuses the browser address bar — suppress that so the
      // triple-press stays inside the app.
      e.preventDefault();
      const now = Date.now();
      if (now - firstTs > WINDOW_MS) { count = 1; firstTs = now; }
      else { count += 1; }
      if (count >= 3) {
        count = 0;
        firstTs = 0;
        openLockdownConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isSuperAdmin, openLockdownConfirm]);

  const applyLockdownToggle = async () => {
    const next = !lockConfirm.current;
    setLockConfirm((s) => ({ ...s, busy: true }));
    try {
      // PUT is a partial update — sending only the flag preserves the message,
      // business-hours schedule and idle-logout policy untouched.
      await api.put('/system/portal-access', { portal_lockdown: next });
      toast.success(next
        ? 'Maintenance Lockdown turned ON — everyone except you is locked out.'
        : 'Maintenance Lockdown turned OFF — the portal is open again.');
      setLockConfirm({ open: false, current: false, busy: false, loading: false });
    } catch {
      toast.error('Failed to toggle Maintenance Lockdown.');
      setLockConfirm((s) => ({ ...s, busy: false }));
    }
  };

  return (
    <>
      {leaderHint && (
        <div
          className="fixed bottom-5 right-5 z-[200] px-3 py-1.5 rounded-md bg-[var(--ink-navy)] text-white text-xs font-mono shadow-lg flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2"
          data-testid="kbd-leader-hint"
        >
          <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-white text-[10px]">G</kbd>
          <span>then a key…</span>
        </div>
      )}

      {isSuperAdmin && (
        <AlertDialog
          open={lockConfirm.open}
          onOpenChange={(o) => { if (!o && !lockConfirm.busy) setLockConfirm((s) => ({ ...s, open: false })); }}
        >
          <AlertDialogContent data-testid="lockdown-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <ShieldAlert size={18} className="text-rose-600" />
                {lockConfirm.loading
                  ? 'Maintenance Lockdown'
                  : (lockConfirm.current ? 'Turn OFF Maintenance Lockdown?' : 'Turn ON Maintenance Lockdown?')}
              </AlertDialogTitle>
              <AlertDialogDescription data-testid="lockdown-confirm-desc">
                {lockConfirm.loading
                  ? 'Checking the current portal status…'
                  : (lockConfirm.current
                    ? 'This reopens the portal. All users will be able to log in and use it again.'
                    : 'This instantly locks out everyone except you (Super Admin) and ends their active sessions. They will not be able to log in until you turn it off.')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="lockdown-confirm-cancel" disabled={lockConfirm.busy}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="lockdown-confirm-apply"
                disabled={lockConfirm.busy || lockConfirm.loading}
                onClick={(e) => { e.preventDefault(); applyLockdownToggle(); }}
                className={lockConfirm.current ? undefined : 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-600'}
              >
                {lockConfirm.busy
                  ? 'Applying…'
                  : (lockConfirm.current ? 'Turn OFF' : 'Turn ON Lockdown')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

export function KeyboardShortcutsButton({ onOpen }) {
  // Lightweight "?" button. Topbar mounts this separately from the listener
  // so the listener can stay alive across re-renders of the topbar.
  return (
    <button
      type="button"
      onClick={onOpen}
      title="Keyboard shortcuts (?)"
      className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100 text-slate-600 transition-colors"
      data-testid="kbd-shortcuts-button"
      aria-label="Open keyboard shortcuts"
    >
      <HelpCircle size={18} />
    </button>
  );
}

export function KeyboardShortcutsDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="kbd-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle>Keyboard Shortcuts</DialogTitle>
          <DialogDescription>
            Press <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] font-mono border border-slate-300">G</kbd>
            {' '}followed by another key to jump anywhere, or open the Quick Launcher with{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] font-mono border border-slate-300">Ctrl</kbd>
            {' '}<span className="text-[11px] text-slate-400">/</span>{' '}
            <kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] font-mono border border-slate-300">⌘</kbd>
            {' '}<kbd className="px-1.5 py-0.5 rounded bg-slate-100 text-[11px] font-mono border border-slate-300">K</kbd>.
            Shortcuts don't fire while you're typing.
          </DialogDescription>
        </DialogHeader>
        <div className="divide-y divide-[var(--divider)]" data-testid="kbd-shortcuts-list">
          {/* Quick Launcher — headline shortcut, shown first. */}
          <div className="flex items-center justify-between py-2.5" data-testid="kbd-shortcut-row-quick-launcher">
            <div className="text-sm text-slate-800">Open Quick Launcher (search anything)</div>
            <div className="flex items-center gap-1.5">
              <kbd className="px-2 py-1 rounded bg-slate-100 text-[11px] font-mono border border-slate-300 min-w-[28px] text-center">Ctrl</kbd>
              <span className="text-[11px] text-slate-400">/</span>
              <kbd className="px-2 py-1 rounded bg-slate-100 text-[11px] font-mono border border-slate-300 min-w-[28px] text-center">⌘</kbd>
              <span className="text-[11px] text-slate-400">then</span>
              <kbd className="px-2 py-1 rounded bg-slate-100 text-[11px] font-mono border border-slate-300 min-w-[28px] text-center">K</kbd>
            </div>
          </div>
          {SHORTCUTS.map((s) => (
            <div key={s.keys.join('+')} className="flex items-center justify-between py-2.5" data-testid={`kbd-shortcut-row-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="text-sm text-slate-800">{s.label}</div>
              <div className="flex items-center gap-1.5">
                {s.keys.map((k, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span className="text-[11px] text-slate-400">then</span>}
                    <kbd className="px-2 py-1 rounded bg-slate-100 text-[11px] font-mono border border-slate-300 min-w-[28px] text-center">
                      {k}
                    </kbd>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between py-2.5">
            <div className="text-sm text-slate-800">Open this help</div>
            <kbd className="px-2 py-1 rounded bg-slate-100 text-[11px] font-mono border border-slate-300">?</kbd>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
